import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { apiInstance } from '../api/api';

const emptyBranch = {
    name: '',
    code: '',
    store_type: 'OUTLET',
    parent_store: '',
    is_active: true,
    receipt_area: '',
    receipt_phone_number: '',
    receipt_payment_details: '',
    printnode_printer_id: '',
};

function Branches(){
    const [stores, setStores] = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [editingStoreId, setEditingStoreId] = useState(null);
    const [form, setForm] = useState(emptyBranch);

    const parentOptions = useMemo(
        () => stores.filter((store) => String(store.id) !== String(editingStoreId)),
        [stores, editingStoreId]
    );

    const loadStores = async () => {
        setLoading(true);
        try {
            const response = await apiInstance.get('/stores/');
            setStores(response.data.results || response.data || []);
        } catch (error) {
            setMessage({
                type: 'danger',
                text: error?.response?.data?.detail || 'Failed to load branches.',
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadStores();
    }, []);

    const openCreateModal = () => {
        setEditingStoreId(null);
        setForm(emptyBranch);
        setShowModal(true);
    };

    const openEditModal = (store) => {
        setEditingStoreId(store.id);
        setForm({
            name: store.name || '',
            code: store.code || '',
            store_type: store.store_type || 'OUTLET',
            parent_store: store.parent_store || '',
            is_active: Boolean(store.is_active),
            receipt_area: store.receipt_area || '',
            receipt_phone_number: store.receipt_phone_number || '',
            receipt_payment_details: store.receipt_payment_details || '',
            printnode_printer_id: store.printnode_printer_id || '',
        });
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingStoreId(null);
        setForm(emptyBranch);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setMessage(null);

        const payload = {
            ...form,
            parent_store: form.parent_store || null,
        };

        try {
            if (editingStoreId) {
                await apiInstance.put(`/stores/${editingStoreId}/`, payload);
                setMessage({ type: 'success', text: 'Branch settings updated.' });
            } else {
                await apiInstance.post('/stores/', payload);
                setMessage({ type: 'success', text: 'Branch created.' });
            }
            closeModal();
            await loadStores();
        } catch (error) {
            const errorData = error?.response?.data;
            const fallback = editingStoreId ? 'Failed to update branch.' : 'Failed to create branch.';
            setMessage({
                type: 'danger',
                text: typeof errorData === 'string' ? errorData : (errorData?.detail || fallback),
            });
        }
    };

    const getParentName = (parentId) => {
        if (!parentId) return '-';
        return stores.find((store) => String(store.id) === String(parentId))?.name || parentId;
    };

    return (
        <div className="container-fluid py-4">
            <Row className="align-items-center mb-4">
                <Col>
                    <h3 className="fw-bold mb-1">Branches</h3>
                    <p className="text-muted mb-0">Store details, receipt settings, and branch printer routing.</p>
                </Col>
                <Col xs="auto">
                    <Button variant="primary" onClick={openCreateModal}>New Branch</Button>
                </Col>
            </Row>

            {message && (
                <Alert variant={message.type} onClose={() => setMessage(null)} dismissible>
                    {message.text}
                </Alert>
            )}

            {loading ? (
                <Spinner animation="border" />
            ) : (
                <Table responsive hover>
                    <thead>
                    <tr>
                        <th>Name</th>
                        <th>Code</th>
                        <th>Type</th>
                        <th>Parent</th>
                        <th>Area</th>
                        <th>Phone</th>
                        <th>Printer ID</th>
                        <th>Status</th>
                        <th>Action</th>
                    </tr>
                    </thead>
                    <tbody>
                    {stores.length ? stores.map((store) => (
                        <tr key={store.id}>
                            <td>{store.name}</td>
                            <td>{store.code}</td>
                            <td>{store.store_type}</td>
                            <td>{store.parent_store_name || getParentName(store.parent_store)}</td>
                            <td>{store.receipt_area || '-'}</td>
                            <td>{store.receipt_phone_number || '-'}</td>
                            <td>{store.printnode_printer_id || '-'}</td>
                            <td>
                                <Badge bg={store.is_active ? 'success' : 'secondary'}>
                                    {store.is_active ? 'Active' : 'Inactive'}
                                </Badge>
                            </td>
                            <td>
                                <Button size="sm" variant="outline-primary" onClick={() => openEditModal(store)}>
                                    Edit
                                </Button>
                            </td>
                        </tr>
                    )) : (
                        <tr>
                            <td colSpan="9" className="text-center text-muted">No branches available</td>
                        </tr>
                    )}
                    </tbody>
                </Table>
            )}

            <Modal show={showModal} onHide={closeModal} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>{editingStoreId ? 'Edit Branch' : 'New Branch'}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Row className="g-3">
                            <Col md={6}>
                                <Form.Label>Name</Form.Label>
                                <Form.Control
                                    value={form.name}
                                    onChange={(e) => setForm({...form, name: e.target.value})}
                                    required
                                />
                            </Col>
                            <Col md={3}>
                                <Form.Label>Code</Form.Label>
                                <Form.Control
                                    value={form.code}
                                    onChange={(e) => setForm({...form, code: e.target.value})}
                                    required
                                />
                            </Col>
                            <Col md={3}>
                                <Form.Label>Type</Form.Label>
                                <Form.Select
                                    value={form.store_type}
                                    onChange={(e) => setForm({...form, store_type: e.target.value})}
                                    required
                                >
                                    <option value="OUTLET">Outlet</option>
                                    <option value="WAREHOUSE">Warehouse</option>
                                </Form.Select>
                            </Col>
                            <Col md={6}>
                                <Form.Label>Parent Store</Form.Label>
                                <Form.Select
                                    value={form.parent_store || ''}
                                    onChange={(e) => setForm({...form, parent_store: e.target.value})}
                                >
                                    <option value="">No parent</option>
                                    {parentOptions.map((store) => (
                                        <option key={store.id} value={store.id}>{store.name}</option>
                                    ))}
                                </Form.Select>
                            </Col>
                            <Col md={6}>
                                <Form.Label>PrintNode Printer ID</Form.Label>
                                <Form.Control
                                    value={form.printnode_printer_id}
                                    onChange={(e) => setForm({...form, printnode_printer_id: e.target.value})}
                                    placeholder="73969712"
                                />
                            </Col>
                            <Col md={6}>
                                <Form.Label>Receipt Area</Form.Label>
                                <Form.Control
                                    value={form.receipt_area}
                                    onChange={(e) => setForm({...form, receipt_area: e.target.value})}
                                    placeholder="Ngong Town, Kajiado"
                                />
                            </Col>
                            <Col md={6}>
                                <Form.Label>Receipt Phone Number</Form.Label>
                                <Form.Control
                                    value={form.receipt_phone_number}
                                    onChange={(e) => setForm({...form, receipt_phone_number: e.target.value})}
                                    placeholder="0789525313"
                                />
                            </Col>
                            <Col md={12}>
                                <Form.Label>Receipt Payment Details</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={4}
                                    value={form.receipt_payment_details}
                                    onChange={(e) => setForm({...form, receipt_payment_details: e.target.value})}
                                    placeholder="BUY GOODS TILL NUMBER: 4030382"
                                />
                            </Col>
                            <Col md={12}>
                                <Form.Check
                                    type="switch"
                                    label="Active"
                                    checked={form.is_active}
                                    onChange={(e) => setForm({...form, is_active: e.target.checked})}
                                />
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="light" onClick={closeModal}>Cancel</Button>
                        <Button variant="primary" type="submit">Save</Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
}

export default Branches;
